import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  CallbackType,
  FRAuth,
  FRLoginFailure,
  FRLoginSuccess,
  FRStep,
  StepOptions,
  StepType,
  TextOutputCallback,
  TokenManager,
} from '@forgerock/javascript-sdk';
import { useDeviceDetect, useExternalScript } from 'Hooks';
import {
  BOTTOM,
  DEFAULT_MESSAGE,
  getErrorMessageByCode,
  getHeaders,
  groupElements,
  HeaderParts,
  mapCallbackToComponent,
  mapStageToExtraComponents,
  MESSAGE_TYPE_ERROR,
  RealFailureDetail,
  TrackingService,
} from 'Services';
import { Configs, Journey, Origin } from 'Constants';
import { displayErrorPage, hasCallbackError, isOtpRetryLimitError, isTimeoutError, useAppContext } from 'Contexts';
import { ErrorMessage, Loader } from 'Components';
import { Icons } from 'Assets';

export interface JourneyContextValue {
  goToNextStep: (options?: StepOptions) => void;
  goBackOneStep: () => void;
  components: React.ReactNode[];
  headers: HeaderParts;
  loading: boolean;
  isAtStage: (stage: Stage) => boolean;
  readOnly: boolean;
  setReadOnly: React.Dispatch<React.SetStateAction<boolean>>;
  loaderElementId: string;
  setLoaderElementId: React.Dispatch<React.SetStateAction<string>>;
  step?: FRStep;
  isStandardSubmit: boolean;
  setStandardSubmit: React.Dispatch<React.SetStateAction<boolean>>;
}

export const JourneyContext = createContext<JourneyContextValue | undefined>(undefined);

export const useJourneyContext = (): JourneyContextValue => {
  const context = useContext(JourneyContext);

  if (!context) {
    throw new Error('useJourneyContext must be within JourneyContextProvider');
  }

  return context;
};

interface Props {
  journey: Journey;
  socialConnect?: SocialConnect;
  children?: React.ReactNode;
}

interface SocialConnect {
  socialConnectCode?: string;
  socialConnectState?: string;
}

export const GO_TO = 'goTo';
export const SOCIAL_CONNECT_PENDING = 'socialConnectPending';

export const JourneyContextProvider: React.FC<Props> = ({ journey, socialConnect, children }) => {
  const [step, setStep] = useState<FRStep>();
  const [previousSteps, setPreviousSteps] = useState<FRStep[]>([]);
  const [goToUrl, setGoToUrl] = useState<URL>();
  const [error, setError] = useState<FRLoginFailure>();
  const [displayedComponents, setDisplayedComponents] = useState<React.ReactNode[]>([]);
  const [headers, setHeaders] = useState<HeaderParts>(getHeaders());
  const [loading, setLoading] = useState(false);
  const [loaderElementId, setLoaderElementId] = useState<string>('');
  const [pageTracked, setPageTracked] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [isStandardSubmit, setStandardSubmit] = useState(true);

  const { mobile } = useDeviceDetect();
  const { origin, basketQuantity, connectionType, kameleoonInjected } = useAppContext();

  const excludedPaths = ['/authentification/callback'];
  const extraData = { 'data-domain-script': `${Configs.tracking.oneTrust.domain}`, 'data-document-language': 'true' };

  useExternalScript(`${Configs.tracking.oneTrust.script}`, excludedPaths, extraData);
  useExternalScript(process.env.REACT_APP_DIRECT_TRACKING !== 'true' ? `${Configs.tracking.adobeAnalyticsScript}` : '', excludedPaths);
  useExternalScript(kameleoonInjected ? `${Configs.tracking.kameleoonScript}` : '', excludedPaths);

  const shouldHideCallbackError = (callback: TextOutputCallback): boolean =>
    callback.getMessage() !== DEFAULT_MESSAGE && callback.getMessageType() === MESSAGE_TYPE_ERROR;

  const startOrResumeJourney = () => {
    if (!origin || !connectionType) return;

    const url = new URL(window.location.href);
    const authIndexValue = url.searchParams.get('authIndexValue');
    const suspendedUuid = url.searchParams.get('suspendedUuid');
    const socConnect = sessionStorage.getItem(SOCIAL_CONNECT_PENDING);

    if (socConnect !== 'true' && socialConnect?.socialConnectCode && socialConnect?.socialConnectState) {
      setLoading(true);
      setError(undefined);
      setNextStep(
        FRAuth.resume(`${window.location.href}?code=${socialConnect.socialConnectCode}&state=${socialConnect.socialConnectState}`, {
          tree: journey,
          query: { origin, connectionType },
        }),
        origin,
      );
      sessionStorage.setItem(SOCIAL_CONNECT_PENDING, 'true');
      return;
    }

    let options = { tree: journey } as StepOptions;

    if (journey === Journey.RESET_PASSWORD && authIndexValue === Journey.RESET_PASSWORD && suspendedUuid)
      options = { tree: journey, query: { suspendedUuid } };

    goToNextStep(options);
  };

  const getOidcTokensAndRedirect: () => void = async () => {
    try {
      await TokenManager.getTokens({ forceRenew: true });
    } catch (err) {
      return;
    }

    const goTo = goToUrl || localStorage.getItem(GO_TO);
    if (goTo) {
      localStorage.removeItem(GO_TO);
      window.location.replace(goTo);
    }
  };

  const saveRedirectUrl = () => {
    const url = new URL(window.location.href);
    const goTo = url.searchParams.get('goto') || localStorage.getItem(GO_TO);

    if (goTo) {
      setGoToUrl(new URL(goTo));
      localStorage.setItem(GO_TO, goTo);
    } else {
      process.env.REACT_APP_MOCK_API !== 'true' && Configs.authorizationUrl && window.location.replace(Configs.authorizationUrl);
    }
  };

  const updatePreviousSteps = (steps: FRStep[]): FRStep[] => {
    if (!step) return [];
    const previousStep = steps[steps.length - 1];
    if (!previousStep) return [step];
    if (step.type === previousStep.type) return [...steps];
    return [...steps, step];
  };

  const goToNextStep = (options?: StepOptions) => {
    if (!origin || !connectionType) return;
    setLoading(true);
    setError(undefined);
    setPageTracked(!options);
    setNextStep(
      FRAuth.next(step, {
        ...options,
        query: { ...options?.query, origin, connectionType },
        tree: journey,
      }),
      origin,
    );
  };

  const setNextStep = (result: Promise<FRStep | FRLoginSuccess | FRLoginFailure>, origin: Origin) => {
    result
      .then((nextStep) => {
        const stage = getStage(step);
        const path = TrackingService.buildPath(stage, mobile, origin);
        setLoading(false);

        switch (nextStep.type) {
          case StepType.Step:
            setPageTracked(false);
            setPreviousSteps(updatePreviousSteps);
            setStep(nextStep);
            break;
          case StepType.LoginSuccess: {
            const eventId = TrackingService.buildEventIdClick(stage, 'success', origin);
            TrackingService.triggerTrackingClic(eventId, path);
            getOidcTokensAndRedirect();
            break;
          }
          case StepType.LoginFailure: {
            const eventId = TrackingService.buildEventIdClick(stage, 'failure', origin);
            TrackingService.triggerTrackingClic(eventId, path);
            setError(nextStep);
            break;
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const goBackOneStep = () => {
    setError(undefined);
    const previousStep = previousSteps.pop();
    if (previousStep) {
      setStep(previousStep);
    } else {
      setStep(undefined);
      startOrResumeJourney();
    }
  };

  const handleHeaders = (stage: Stage, headers: HeaderParts, withCallbackError: boolean) => {
    let stageHeaders = getHeaders(stage);
    if (withCallbackError) {
      stageHeaders = {
        ...stageHeaders,
        subTitle: '',
      };
    }
    if (stage === 'Redirect') {
      stageHeaders = {
        ...stageHeaders,
        title: headers.title,
      };
    }
    setHeaders(stageHeaders);
  };

  const sortElements = (allElements: JSX.Element[]) =>
    allElements.sort((firstElement, secondElement) => {
      if (firstElement.key !== null && (firstElement.key as string).endsWith(BOTTOM)) {
        return 1;
      }

      if (secondElement.key !== null && (secondElement.key as string).endsWith(BOTTOM)) {
        return -1;
      }

      return 0;
    });

  const getStage = (step: FRStep | undefined) =>
    (step?.getStage() ??
      step?.callbacks
        .find((callback) => callback.getType() === 'HiddenValueCallback' && callback.getInputValue() === 'stage')
        ?.getOutputValue()) as Stage;

  const isAtStage = (stage: Stage): boolean => (step ? (step.getStage() as Stage) === stage : false);

  const buildErrorMessageByStage = (stage: Stage) => {
    if (stage === 'Captcha Page') {
      return 'Captcha failure';
    }
    return error?.getMessage();
  };

  const buildErrorElement = (stage: Stage): JSX.Element => {
    const errorMessage = buildErrorMessageByStage(stage);
    const errorMessageByCode = getErrorMessageByCode(
      error?.getCode() ?? 500,
      (error?.getDetail() as RealFailureDetail)?.errorCode ?? errorMessage ?? 'default',
    );
    return <ErrorMessage key="error" message={errorMessageByCode} />;
  };

  useEffect(() => {
    saveRedirectUrl();
    startOrResumeJourney();
  }, [origin, connectionType]);

  useEffect(() => {
    if (!origin) return;

    const isRedirect = !!step?.getCallbacksOfType(CallbackType.RedirectCallback).pop();
    if (isRedirect) {
      setDisplayedComponents([<Loader key="loading" icon={Icons.SpinnerBlue} />]);
      handleHeaders('Redirect', headers, false);
      step && FRAuth.redirect(step);
      return;
    }

    const displayedComponents: JSX.Element[] = [];
    const stage = getStage(step);
    const isSocial = !!step?.callbacks.find(
      (callback) => callback.getType() === 'HiddenValueCallback' && callback.getInputValue() === 'isSocial',
    );

    const callbacksToRender =
      error && step?.callbacks
        ? step.callbacks.filter((callback) => !(callback instanceof TextOutputCallback && shouldHideCallbackError(callback)))
        : step?.callbacks;

    const mappedComponents = callbacksToRender
      ?.map((callback) => mapCallbackToComponent(callback, stage, origin, mobile, isSocial))
      .filter(Boolean) as JSX.Element[];
    displayedComponents.push(...groupElements(mappedComponents || [<Loader key="loading" icon={Icons.SpinnerBlue} />]));

    if (error) {
      if (isOtpRetryLimitError(error, stage)) {
        setNextStep(FRAuth.next(undefined, { tree: journey } as StepOptions), origin);
        return;
      } else if (isTimeoutError(error)) {
        const path = TrackingService.buildPath(stage, mobile, origin);
        const eventId = TrackingService.buildEventIdClick(stage, 'timeout', origin);
        TrackingService.triggerTrackingClic(eventId, path);
      }
      displayedComponents.push(buildErrorElement(stage));
    }

    const withCallbackError = hasCallbackError(step);
    const withErrorPage = displayErrorPage(withCallbackError, stage);
    const extraElements = step ? mapStageToExtraComponents(step, origin, mobile, withErrorPage) : [];
    const allElements = [...displayedComponents, ...extraElements];
    const allElementsSorted = sortElements(allElements);
    setDisplayedComponents(allElementsSorted);
    handleHeaders(stage, headers, withErrorPage);
  }, [mobile, origin, step, error]);

  useEffect(() => {
    const stage = getStage(step);
    if (isPageTracked() || !stage || !origin) return;
    TrackingService.trackPageData(stage, mobile, basketQuantity > 0, origin);
    setPageTracked(true);
  }, [pageTracked, step, mobile, basketQuantity, origin]);

  const isPageTracked = () => {
    const hasLoginError =
      step?.callbacks
        ?.filter(
          (callback) =>
            callback instanceof TextOutputCallback &&
            callback.getMessage() !== DEFAULT_MESSAGE &&
            callback.getMessageType() === MESSAGE_TYPE_ERROR,
        )
        .pop() !== undefined;
    return pageTracked || hasLoginError;
  };

  const value = useMemo(
    () => ({
      goToNextStep,
      goBackOneStep,
      components: displayedComponents,
      headers,
      loading,
      isAtStage,
      readOnly,
      setReadOnly,
      loaderElementId,
      setLoaderElementId,
      step,
      isStandardSubmit,
      setStandardSubmit,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayedComponents, headers, loading, readOnly, loaderElementId, step, isStandardSubmit],
  );

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
};
